import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createAssetProcessingDoc,
  exportDomToPdf,
  failAssetDoc,
  finalizeAssetDoc,
  uploadBlobToStorage,
} from './printablesExport';

const PAGE_SIZE_MM = { width: 210, height: 297 };

const qrImageUrl = (value) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(
    value || '',
  )}`;

const WelcomeOnePagerPrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
  compliance,
  destinationUrl,
}) => {
  const pageRef = useRef(null);
  const [saveToAssets, setSaveToAssets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [linkId, setLinkId] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  const missing = useMemo(() => {
    const list = [];
    if (compliance.requirePhone && !profile.phone) list.push('phone');
    if (compliance.requireAddress && !branch.address) list.push('address');
    if (compliance.requireRera && !branch.reraId) list.push('rera');
    return list;
  }, [branch.address, branch.reraId, compliance, profile.phone]);

  const isBlocked = missing.length > 0 || !shortUrl;
  const canSave = Boolean(tenantId && branchId && authUser?.uid);
  const accent = brandKit?.colors?.accent || '#c8a951';

  useEffect(() => {
    if (!tenantId || !firebase?.functions || !destinationUrl || shortUrl || linkLoading) return;
    const createLink = async () => {
      setLinkLoading(true);
      setLinkError('');
      try {
        const callable = firebase.functions.httpsCallable('createShortLink');
        const result = await callable({
          longUrl: destinationUrl,
          utm: { source: 'welcome_onepager' },
          branchId,
        });
        const data = result?.data || {};
        setShortUrl(data.shortUrl || '');
        setLinkId(data.linkId || '');
      } catch (err) {
        console.error('Welcome kit link failed:', err);
        setLinkError('Unable to generate QR link.');
      } finally {
        setLinkLoading(false);
      }
    };
    createLink();
  }, [branchId, destinationUrl, firebase, linkLoading, shortUrl, tenantId]);

  const handleExport = async () => {
    if (isBlocked || !pageRef.current) return;
    setExporting(true);
    setError('');
    setWarning('');
    setMessage('');

    let assetRef = null;
    try {
      const fileName = `Douglas-McCartney-Welcome-Kit-${profile.fullName || 'franchisee'}.pdf`;
      const blob = await exportDomToPdf({
        element: pageRef.current,
        fileName,
        pageSizeMm: PAGE_SIZE_MM,
        backgroundColor: '#ffffff',
        marginMm: 8,
      });

      if (saveToAssets) {
        if (!canSave) {
          setWarning('Upload failed, file not saved.');
        } else {
          try {
            const snapshot = {
              fullName: profile.fullName || '',
              designation: profile.designation || '',
              phone: profile.phone || '',
              assignedEmail: assignedEmail || '',
              branchName: branch.branchName || '',
              address: branch.address || '',
              city: branch.city || '',
              reraId: branch.reraId || '',
              linkId,
              brand: {
                colors: brandKit?.colors || {},
                disclaimerText: brandKit?.disclaimerText || '',
                logoUrl: brandKit?.logoUrl || '',
              },
            };

            assetRef = await createAssetProcessingDoc({
              firebase,
              tenantId,
              branchId,
              authUserId: authUser?.uid || '',
              type: 'welcome_onepager',
              snapshot,
            });

            const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/welcome-onepager.pdf`;
            const url = await uploadBlobToStorage({
              firebase,
              storagePath,
              blob,
              contentType: 'application/pdf',
              metadata: { tenantId, branchId, createdBy: authUser?.uid || '' },
            });

            await finalizeAssetDoc({
              firebase,
              assetRef,
              output: { pdfUrl: url, storagePath, linkId },
            });
          } catch (uploadError) {
            console.error('Welcome kit asset upload failed:', uploadError);
            setWarning('Upload failed, file not saved.');
            if (assetRef) {
              await failAssetDoc({ firebase, assetRef, errorMessage: uploadError.message });
            }
          }
        }
      }
      setMessage('Welcome kit downloaded.');
    } catch (err) {
      console.error('Welcome kit export failed:', err);
      setError('Could not generate PDF. Try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="printable-layout">
      <div className="printable-preview">
        <div className="printable-page" ref={pageRef}>
          <div className="page-inner">
            <div className="printable-header">
              <div className="printable-logo">
                {brandKit?.logoUrl ? (
                  <img src={brandKit.logoUrl} alt="Logo" crossOrigin="anonymous" />
                ) : (
                  <div className="printable-logo-text">
                    <span>Douglas &amp;</span>
                    <span>McCartney</span>
                  </div>
                )}
              </div>
              <div className="printable-contact">
                <div>{profile.phone || 'Phone needed'}</div>
                <div>{assignedEmail || 'Email pending'}</div>
              </div>
            </div>

            <div className="printable-hero">
              <h2 className="printable-title">Welcome</h2>
              <div className="printable-subtitle">Your franchise onboarding companion</div>
            </div>

            <div className="welcome-section" style={{ borderColor: accent }}>
              <h3>How we help</h3>
              <ul className="welcome-bullets">
                <li>Qualified leads and listing support.</li>
                <li>Marketing assets and campaign guidance.</li>
                <li>On-ground training and operational playbooks.</li>
              </ul>
            </div>

            <div className="welcome-section" style={{ borderColor: accent }}>
              <h3>Our services</h3>
              <div className="welcome-services">
                <span>Sales</span>
                <span>Resale</span>
                <span>Rentals</span>
                <span>Home Loans</span>
                <span>Legal Support</span>
              </div>
            </div>

            <div className="welcome-section" style={{ borderColor: accent }}>
              <h3>Contact</h3>
              <div className="welcome-contact">
                <span>{profile.phone || 'Phone required'}</span>
                <span>{assignedEmail || 'Email pending'}</span>
                <span>{branch.address || 'Address required'}</span>
              </div>
            </div>

            <div className="printable-qr">
              {shortUrl ? (
                <>
                  <img src={qrImageUrl(shortUrl)} alt="QR Code" crossOrigin="anonymous" />
                  <div className="qr-url">{shortUrl}</div>
                </>
              ) : (
                <div className="printable-subtitle">
                  {linkLoading ? 'Generating QR link...' : 'QR link not available'}
                </div>
              )}
            </div>

            <div className="printable-footer">
              <strong>{branch.branchName || 'Douglas & McCartney'}</strong>
              {brandKit?.disclaimerText && <div>{brandKit.disclaimerText}</div>}
              {branch.reraId && <div>RERA: {branch.reraId}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="printable-panel">
        <div className={`printable-status ${isBlocked ? 'error' : 'success'}`}>
          {isBlocked ? 'Missing Requirements' : 'Ready'}
        </div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {linkError && <div className="printable-banner error">{linkError}</div>}
        {missing.length > 0 && (
          <div className="printable-banner error">
            Missing: {missing.join(', ')}.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}

        <div className="printable-detail">
          <span>Short Link</span>
          <strong>{shortUrl || 'Generating...'}</strong>
        </div>
        <div className="printable-detail">
          <span>Phone</span>
          <strong>{profile.phone || 'Missing'}</strong>
        </div>
        <div className="printable-detail">
          <span>Address</span>
          <strong>{branch.address || 'Missing'}</strong>
        </div>

        <div className="printable-actions">
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={saveToAssets}
              onChange={(event) => setSaveToAssets(event.target.checked)}
            />
            Save to Assets Library
          </label>
          <button
            type="button"
            className="printable-action"
            disabled={exporting || isBlocked}
            onClick={handleExport}
          >
            {exporting ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeOnePagerPrintable;
